import sounds from 'src/lib/sounds';
import View from '@play-co/timestep-core/lib/ui/View';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import { user } from 'src/StateObserver';
import { UserSettings } from 'src/replicant/State';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import PopupBasicLazy from '../../components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { FooterLinks, FooterLinksOpts } from '../components/FooterLinks';
import { SettingsCheckboxGroup } from '../components/SettingsCheckboxGroup';
import { SettingDebugOverlay } from '../components/SettingsDebugOverlay';
import { FooterCTAs } from '../components/FooterCTAs';
import { SettingsToggleSwitcher } from '../components/SettingsToggleSwitcher';

const assets = {
  icon: 'assets/ui/menu/settings_icon.png',
};

export type SettingsKey =
  | Exclude<keyof UserSettings, 'receiveMessages'>
  | 'notifications';
type Settings = UserSettings;

type PopupSettingsOpts = {
  isNative: boolean;
  hasWebIap: boolean;
  isGuest: boolean;
  isDevelopment: boolean;
};

const defaultHeight = 824;

export default class PopupSettings extends PopupBasicLazy {
  private settings: Settings;
  private content: FlexBoxView;
  private close: () => void;

  public getAssetsGroup() {
    return new AssetGroup(
      AssetGroup.constructURLs([
        'assets/ui/menu/',
        'assets/ui/shared/',
        'assets/ui/demo/', // For LINE button
      ]),
    );
  }

  constructor(opts: { superview: View; close: () => void }) {
    super({
      ...opts,
      width: 591,
      height: defaultHeight,
      offsetY: 20,
      tracking: { feature: 'settings', $subFeature: 'popup-settings' },
      titleIcon: { image: assets.icon },
    });

    this.close = opts.close;

    // Get stored settings
    const userSettings = user().settings;
    this.settings = {
      ...userSettings,
      pushNotifications: false,
    };

    // Create flexbox wrapper for content
    this.content = new FlexBoxView({
      superview: this.box,
      flex: {
        width: this.box.style.width,
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 70,
      },
    });

    // Adjust box height on content reflow
    const originalReflow = this.content.reflow.bind(this.content);
    this.content.reflow = (reason) => {
      originalReflow(reason);
      this.box.updateOpts({
        height: this.content.style.height + 35,
        centerOnOrigin: true,
      });
    };
  }

  public init(opts: PopupSettingsOpts) {
    super.init(opts);
    this.title.setText(() => i18n('settings.title').toLocaleUpperCase());

    /**
     * Override title label flex width to be a fixed width,
     * preventing it from scaling down on locale change.
     */
    getFlexNode(this.title.title).width = 340;

    const { hasWebIap, isGuest, isDevelopment } = opts;

    // Setting keys in the order they should appear in the settings.
    const keys: SettingsKey[] = ['music', 'sound', 'notifications', 'locale'];

    // Create setting items
    keys.forEach((key) => this.createSettingRow(key));

    // Create CTAs column
    this.createFooterCTAsSection(isGuest);

    // Create footer links
    this.createFooterLinksSection(hasWebIap);

    // Create close CTA
    this.createCloseButton();

    // Add debug overlay in development mode
    if (isDevelopment) {
      new SettingDebugOverlay(this.root);
    }
  }

  private createSettingRow(key: SettingsKey) {
    const container = new FlexBoxView({
      superview: this.content,
      flex: {
        flexDirection: 'column',
        width: '100%',
        height: 100,
        paddingHorizontal: 14,
      },
    });

    const content = new FlexBoxView({
      superview: container,
      flex: {
        width: '100%',
        paddingLeft: 15,
        paddingRight: 15,
        flex: 1,
        justifyContent: 'space-between',
        alignItems: 'center',
      },
    });

    const label = new LangBitmapFontTextView({
      superview: content,
      height: 40,
      align: 'left',
      verticalAlign: 'center',
      size: 31,
      color: '#BF8147',
      font: 'Body',
      localeText: () => i18n(`settings.${key}`),
    });

    getFlexNode(label).flex = 1;

    switch (key) {
      case 'locale':
      case 'notifications':
      case 'decorationEditor':
        new SettingsCheckboxGroup({
          superview: content,
          type: key,
        });
        break;
      case 'music':
      case 'sound': {
        const toggleSwitcher = new SettingsToggleSwitcher({
          superview: content,
          type: key,
          onToggle: async (value: boolean) => {
            // Set local cache
            this.settings[key] = value;
          },
          isOn: this.settings[key] ?? false,
        });
        getFlexNode(toggleSwitcher).marginRight = 22;
        break;
      }
    }

    this.createRowSeparator(container);
  }

  private createFooterCTAsSection(isGuest: boolean) {
    new FooterCTAs({
      superview: this.content,
      items: isGuest ? ['login', 'support'] : ['faq'],
    });
  }

  private createFooterLinksSection(hasWebIap: boolean) {
    const footerItems: FooterLinksOpts['items'] = ['privacy', 'tos'];

    if (hasWebIap) {
      footerItems.push('tosJpKk', 'tokusyoho', 'kessai', 'rakutenpay');
    }

    footerItems.push('purchaseInfo');

    new FooterLinks({
      superview: this.content,
      items: footerItems,
    });
  }

  private createRowSeparator(superview: View) {
    new FlexBoxView({
      superview,
      flex: {
        width: '100%',
        height: 5,
      },
      backgroundColor: '#FDC381',
    });
  }

  private createCloseButton() {
    // Remove the default popup close button
    this.buttonClose.removeFromSuperview();

    // Add a close CTA button
    new ButtonScaleViewWithText({
      superview: this.content,
      ...uiConfig.buttons.cancel,
      fontSize: 40,
      labelOffsetY: -2,
      font: 'Body',
      width: 235,
      height: 90,
      x: this.content.style.width / 2,
      y: this.content.style.height - 90,
      localeText: () => i18n('basic.back'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return this.close();
      },
      tracking: { feature: 'settings', $subFeature: 'popup-settings-back' },
    });
  }
}
